/**
 * MSR slice 4: start and close each event DAY (migrations 2026-10-11-500 / 501).
 *
 * Only events in the MSR date model (msr_date_model_active) have days to start and close; for every other event
 * get_event_day_run_state returns { model_active: false } and the UI shows nothing new. The server re-checks every
 * rule (who is tagged that day, the day clock in the event's timezone, the checklist, the attendance source, the
 * "No sales today" confirmation) — the flags below only decide what to offer.
 */
import { supabase } from "./supabase";

export type DayStatus =
  | "not_started"
  | "started"
  | "closed"
  | "late"
  | "not_tracked";
export type DayPhase = "upcoming" | "today" | "ended";
export type AttendanceSource = "manual" | "checkins" | "none";

export interface DayVerifiedSale {
  contact_id?: string;
  name: string;
  rep_name: string | null;
  verified_at: string | null;
  /** closed days: added by an admin correction (not in the close's snapshot) */
  added_by_correction?: boolean;
}

export interface DayClose {
  closed_by_name: string | null;
  closed_at: string;
  /** mig 1631: on_behalf = a director / admin closed it on the day for the lead; auto = it closed itself at midnight */
  closed_as: "rep" | "manager" | "on_behalf" | "auto";
  is_late: boolean;
  /** mig 1631: the rep a director / admin closed it for */
  on_behalf_of_name?: string | null;
  /** mig 1631: a day that closed itself before anyone entered its attendance (class / seminar / speaking) */
  attendance_missing?: boolean;
  attendance_source: AttendanceSource;
  attendance_count: number | null;
  checkins_count: number;
  signups_count: number;
  verified_count: number;
  close_rate: number | null;
  no_sales_confirmed: boolean;
  corrections: number;
  latest_correction: {
    attendance_count: number | null;
    verified_count: number;
    close_rate: number | null;
    corrected_at: string;
    by_name: string | null;
    reason: string;
  } | null;
  /** the day's EFFECTIVE sales: the close's snapshot with every correction's adds/removes applied */
  sales: DayVerifiedSale[];
  /** snapshot sales a correction removed */
  removed_sales?: number;
  /** admins only: the day's sign-ups a correction could add */
  addable_signups?: { contact_id: string; name: string }[] | null;
}

export interface DayLive {
  checkins_count: number;
  registered_checkins: number;
  walkin_checkins: number;
  signups_count: number;
  verified_count: number;
  verified: DayVerifiedSale[];
}

export interface EventRunDay {
  event_day_id: string;
  service_date: string; // YYYY-MM-DD, event-local
  window_start: string;
  window_end: string;
  close_by: string;
  phase: DayPhase;
  status: DayStatus;
  i_am_tagged: boolean;
  reps: { name: string | null; role: "lead" | "assigned" }[];
  start: {
    started_by_name: string | null;
    started_at: string;
    by_me: boolean;
    /** mig 1631 */
    start_kind?: "self" | "on_behalf";
    on_behalf_of_name?: string | null;
  } | null;
  close: DayClose | null;
  late_flag: {
    flagged_at: string;
    notify_route: "direct_manager" | "superadmin_fallback" | "nobody";
    notified_count: number;
    resolved_at: string | null;
  } | null;
  live: DayLive | null;
  can_start: boolean;
  start_block: string | null;
  can_close: boolean;
  close_block: string | null;
  close_as: "rep" | "manager" | "on_behalf" | null;
  can_correct: boolean;
  // ---- mig 1631: on behalf, auto-close, alert (absent on a server without 1631)
  /** self = the viewer is on the day's list; on_behalf = a director / admin starts it for acting_for_name */
  start_as?: "self" | "on_behalf" | null;
  acting_for_name?: string | null;
  /** an auto-closed day without attendance: the viewer (its closer, a director or an admin) may enter it */
  can_enter_attendance?: boolean;
  /** when the day closes itself if nobody closes it (null when auto-close is off or the day is closed) */
  auto_close_at?: string | null;
  /** the forgotten-close alert sent to the TM / RM */
  close_alert?: {
    alerted_at: string;
    route: string;
    notified: { name: string | null; why: string }[];
  } | null;
  // ---- mig 1330: rep parts, lead-only close, lead reopen (absent on a server without 1330)
  /** the viewer closes this day: the rep named to close it (primary) or the event lead (backup; the closer when nobody is named) */
  i_am_day_closer?: boolean;
  /** closers' names, primary first */
  closer_names?: string[];
  closers?: {
    name: string | null;
    closer_role: "primary" | "backup";
    why: "named_closer" | "lead";
  }[];
  team?: DayTeamMember[];
  my_part?: { session_id: string; started_at: string } | null;
  can_start_part?: boolean;
  part_block?: string | null;
  can_close_part?: boolean;
  can_reopen?: boolean;
  reopen_block?: string | null;
  reopen_until?: string | null;
  reopens?: { reopened_at: string; by_name: string | null; reason: string }[];
}

/** One rep's own part of the day: part times and their OWN credited sales only (no per-rep attendance / close rate). */
export interface DayTeamMember {
  profile_id: string;
  name: string | null;
  role: "lead" | "assigned" | null;
  on_list: boolean;
  is_day_closer: boolean;
  /** primary = closes today (the named closer, or the lead when nobody is named); backup = the lead behind a named closer */
  closer_role: "primary" | "backup" | null;
  is_me: boolean;
  part_open: boolean;
  part_minutes: number;
  parts: {
    started_at: string;
    closed_at: string | null;
    auto_closed: boolean | null;
    close_kind:
      | "self"
      | "day_close"
      | "late_day_close"
      | "seat_changed"
      | "day_ended"
      | null;
    /** only the part's owner and full-time staff get the note */
    notes: string | null;
  }[];
  /** null before the day has numbers, for a rep no longer on the list, or when hidden from this viewer */
  credited_sales: number | null;
  /** a rep's sales are shown only to that rep, the day's closer, an event lead and full-time staff */
  credited_sales_hidden?: boolean;
}

export interface EventRunState {
  model_active: boolean;
  event_id: string;
  event_type?: string;
  event_status?: string;
  timezone?: string;
  local_today?: string;
  attendance_source?: AttendanceSource;
  has_close_rate?: boolean;
  viewer?: {
    is_full_time: boolean;
    is_admin: boolean;
    is_rep: boolean;
    /** mig 1631: a director or admin (may start / close on the lead's behalf, correct attendance) */
    is_on_behalf_actor?: boolean;
  };
  all_days_closed?: boolean;
  signups_outside_days?: number;
  days?: EventRunDay[];
}

export const dayRunKeys = {
  state: (eventId: string) => ["event-day-run-state", eventId] as const,
};

/** A day start/close refusal: readable message + the server's SQLSTATE and MSR code (for callers that branch). */
export class DayRunError extends Error {
  readonly sqlstate: string | undefined;
  readonly msrCode: string | undefined;
  constructor(err: { message?: string; code?: string }) {
    super(dayRunErrorMessage(err));
    this.name = "DayRunError";
    this.sqlstate = err?.code;
    this.msrCode = /^([A-Z_]+):/.exec(err?.message ?? "")?.[1];
  }
}

/** A refusal that will not change by asking again (not on the event / no permission): stop polling and retrying. */
export function isPermissionRefusal(e: unknown): boolean {
  if (!(e instanceof DayRunError)) {
    return false;
  }
  return (
    e.sqlstate === "42501" ||
    e.msrCode === "MSR_DAY_NOT_PERMITTED" ||
    e.msrCode === "ACTIVE_PROFILE_REQUIRED"
  );
}

export async function getEventDayRunState(
  eventId: string,
): Promise<EventRunState> {
  const { data, error } = await supabase.rpc("get_event_day_run_state", {
    p_event_id: eventId,
  });
  if (error) {
    throw new DayRunError(error);
  }
  return data as EventRunState;
}

export async function startEventDay(
  eventDayId: string,
  checklist: { atVenue: boolean; boothSetUp: boolean; qrSignWorking: boolean },
): Promise<{
  already_started: boolean;
  start_kind?: "self" | "on_behalf";
  on_behalf_of_name?: string | null;
}> {
  const { data, error } = await supabase.rpc("msr_start_event_day", {
    p_event_day_id: eventDayId,
    p_at_venue: checklist.atVenue,
    p_booth_set_up: checklist.boothSetUp,
    p_qr_sign_working: checklist.qrSignWorking,
  });
  if (error) {
    throw new DayRunError(error);
  }
  return data as {
    already_started: boolean;
    start_kind?: "self" | "on_behalf";
    on_behalf_of_name?: string | null;
  };
}

export async function closeEventDay(
  eventDayId: string,
  input: {
    attendance: number | null;
    noSalesConfirmed: boolean;
    expectedVerifiedCount: number | null;
  },
): Promise<void> {
  const { error } = await supabase.rpc("msr_close_event_day", {
    p_event_day_id: eventDayId,
    p_attendance: input.attendance,
    p_no_sales_confirmed: input.noSalesConfirmed,
    p_expected_verified_count: input.expectedVerifiedCount,
  });
  if (error) {
    throw new DayRunError(error);
  }
}

/** mig 1631: enter attendance ONCE on a day that closed itself at midnight (its closer, a director or an admin). */
export async function enterDayAttendance(
  eventDayId: string,
  attendance: number,
): Promise<void> {
  const { error } = await supabase.rpc("event_day_enter_attendance", {
    p_event_day_id: eventDayId,
    p_attendance: attendance,
  });
  if (error) {
    throw new DayRunError(error);
  }
}

/** mig 1330: start the caller's OWN part of a started day (optional). */
export async function startMyPart(
  eventDayId: string,
): Promise<{ already_open: boolean }> {
  const { data, error } = await supabase.rpc("msr_start_my_part", {
    p_event_day_id: eventDayId,
  });
  if (error) {
    throw new DayRunError(error);
  }
  return data as { already_open: boolean };
}

/** mig 1330: close the caller's OWN open part (there is no way to name another rep's part). */
export async function closeMyPart(
  eventDayId: string,
  notes: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("msr_close_my_part", {
    p_event_day_id: eventDayId,
    p_notes: notes && notes.trim() !== "" ? notes.trim() : null,
  });
  if (error) {
    throw new DayRunError(error);
  }
}

/** mig 1330: the day's lead reopens a day they closed (on the day only; reason required). */
export async function reopenEventDay(
  eventDayId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc("msr_reopen_event_day", {
    p_event_day_id: eventDayId,
    p_reason: reason,
  });
  if (error) {
    throw new DayRunError(error);
  }
}

export async function correctEventDayClose(
  eventDayId: string,
  input: {
    attendance: number | null;
    verifiedCount: number | null;
    reason: string;
    /** F4: add / remove the day's sale rows (contact ids); the close's original snapshot is kept */
    addContactIds?: string[];
    removeContactIds?: string[];
  },
): Promise<void> {
  const { error } = await supabase.rpc("admin_correct_event_day_close", {
    p_event_day_id: eventDayId,
    p_attendance: input.attendance,
    p_verified_count: input.verifiedCount,
    p_reason: input.reason,
    p_add_contact_ids: input.addContactIds?.length ? input.addContactIds : null,
    p_remove_contact_ids: input.removeContactIds?.length
      ? input.removeContactIds
      : null,
  });
  if (error) {
    throw new DayRunError(error);
  }
}

const MESSAGES: Record<string, string> = {
  MSR_START_CHECKLIST_INCOMPLETE:
    "Check all three items before starting the day.",
  MSR_START_NOT_TAGGED:
    "You're not on the Sales Reps list for this day. Ask your manager to add you for this day.",
  MSR_START_NOT_TODAY: "A day can only be started on that day.",
  MSR_START_PREVIOUS_UNCLOSED:
    "Finish the other event you're on today first: close the day you started and close, or close your part there.",
  MSR_DAY_ALREADY_CLOSED: "This day is already closed.",
  MSR_DAY_EVENT_NOT_ACTIVE:
    "This event is not active (cancelled, postponed or deleted).",
  MSR_DAY_NOT_IN_MODEL: "This event doesn't use day start and close.",
  MSR_DAY_NOT_TRACKED:
    "This day isn't tracked for start and close (before tracking began, or no rep on it).",
  MSR_DAY_NOT_FOUND: "That event day no longer exists. Refresh the page.",
  MSR_DAY_NOT_PERMITTED: "You're not on this event.",
  MSR_CLOSE_NOT_STARTED: "Start the day before closing it.",
  MSR_CLOSE_NOT_TAGGED:
    "Only a rep on the Sales Reps list for this day can close it today.",
  MSR_CLOSE_DAY_ENDED:
    "This day has ended. Your manager will complete the close.",
  MSR_CLOSE_MANAGER_ONLY:
    "After the day ends, only a full-time employee can complete the close.",
  MSR_CLOSE_TOO_EARLY: "This day hasn't started yet.",
  MSR_CLOSE_ALREADY_CLOSED: "This day is already closed.",
  MSR_CLOSE_LEAD_ONLY:
    "Only the rep who closes this day (the named closer, or the lead) closes it. Close your own part instead.",
  MSR_PART_OTHER_OPEN: "Close your part at the other event first.",
  MSR_PART_NOT_TAGGED:
    "You're not on the Sales Reps list for this day, so you have no part to start.",
  MSR_PART_DAY_NOT_STARTED:
    "The day hasn't been started yet. Start the day first.",
  MSR_PART_NOT_TODAY: "A part can only be started on that day.",
  MSR_PART_NOT_OPEN: "You have no open part on this day.",
  MSR_PART_NOTES_TOO_LONG: "Keep the note under 500 characters.",
  MSR_REOPEN_LEAD_ONLY:
    "Only the rep who closes this day (the named closer, or the lead) can reopen it.",
  MSR_REOPEN_FINALIZED:
    "This event's sales credit is finalized, so the day can't be reopened. An admin can correct it.",
  MSR_REOPEN_NOT_CLOSED: "This day isn't closed.",
  MSR_REOPEN_MANAGER_CLOSE:
    "A day completed by a full-time employee can't be reopened. An admin can correct it.",
  MSR_REOPEN_CORRECTED:
    "An admin already corrected this day, so it can't be reopened.",
  MSR_REOPEN_WINDOW_PASSED:
    "It's too close to the end of the day to reopen it. An admin can correct the closed day.",
  MSR_REOPEN_REASON_REQUIRED: "Say why you're reopening the day.",
  MSR_REOPEN_EVENT_NOT_ACTIVE: "This event is no longer active.",
  MSR_CLOSE_ATTENDANCE_REQUIRED: "Enter the total number of attendees.",
  MSR_CLOSE_ATTENDANCE_INVALID:
    "Attendance must be a whole number from 0 to 100,000.",
  MSR_CLOSE_ATTENDANCE_NOT_ENTERED:
    "Attendance for this event type comes from check-ins.",
  MSR_CLOSE_ZERO_SALES_UNCONFIRMED:
    'There are no verified sales for this day. Tick "No sales today" to close.',
  MSR_CLOSE_COUNTS_CHANGED:
    "Verified sales changed while you were closing. Review the numbers and close again.",
  MSR_CORRECTION_ADMIN_ONLY: "Only an admin can correct a closed day.",
  MSR_CORRECTION_NOT_CLOSED: "Only a closed day can be corrected.",
  MSR_CORRECTION_REASON_REQUIRED:
    "Give a reason for the correction (at least 5 characters).",
  MSR_CORRECTION_EMPTY: "Enter at least one corrected number.",
  MSR_CORRECTION_INVALID: "Numbers must be between 0 and 100,000.",
  MSR_CORRECTION_NO_ATTENDANCE: "This event type has no attendance to correct.",
  MSR_CORRECTION_SALE_NOT_IN_POOL:
    "That person didn't sign up for this event on this day.",
  MSR_CORRECTION_SALE_NOT_ON_DAY: "That person isn't one of this day's sales.",
  MSR_CORRECTION_SALE_ALREADY_ON_DAY:
    "That person is already one of this day's sales.",
  MSR_CORRECTION_COUNT_MISMATCH:
    "With these sale changes the verified count is set for you — leave it blank.",
  MSR_DAY_BUSY:
    "Someone else is changing this event right now. Try again in a moment.",
  MSR_LOCK_FOREIGN: "The event is busy. Try again in a moment.",
  MSR_LOCK_ORDER: "The event is busy. Try again in a moment.",
  MSR_TXN_NOT_CLEAN: "The event is busy. Try again in a moment.",
  MSR_DAY_HAS_RECORD:
    "That date change would remove a day that was already started or closed.",
  MSR_DAY_CLOSED_VERIFY:
    "This sign-up is dated on a day that's already closed. An admin can correct that day.",
  ACTIVE_PROFILE_REQUIRED: "Your account is not active.",
};

/** Turn a server refusal into words. The server message starts with a stable code (MSR_*). */
export function dayRunErrorMessage(err: {
  message?: string;
  code?: string;
}): string {
  const msg = err?.message ?? "";
  const code = /^([A-Z_]+):/.exec(msg)?.[1];
  if (code && MESSAGES[code]) {
    return MESSAGES[code];
  }
  if (err?.code === "55P03" || /lock timeout/i.test(msg)) {
    return "The event is busy. Try again in a moment.";
  }
  return msg || "Something went wrong. Try again.";
}

/** "33%" style close rate, or null when there is none. */
export function formatCloseRate(
  rate: number | null | undefined,
): string | null {
  if (rate === null || rate === undefined) {
    return null;
  }
  return `${Math.round(rate * 1000) / 10}%`;
}

/** Live close-rate preview the MSR sees before closing (the server computes the stored one). */
export function previewCloseRate(
  hasCloseRate: boolean,
  verified: number,
  attendance: number | null,
): number | null {
  if (!hasCloseRate || attendance === null || attendance <= 0) {
    return null;
  }
  return Math.round((verified / attendance) * 10000) / 10000;
}

export const STATUS_LABELS: Record<DayStatus, string> = {
  not_started: "Not started",
  started: "Started",
  closed: "Closed",
  late: "Late — not closed",
  not_tracked: "Not tracked",
};
